import React, { useState } from 'react';
import {
  Badge, Button, ConfirmDialog, DataTable, Input, Modal, PageHead, Switch, Textarea, num,
} from '../components/ui';
import useList from '../hooks/useList';
import { reasonApi } from '../api';
import { useToast } from '../context/ToastContext';

const KINDS = [
  { value: 'cancellation', label: 'Order cancellation' },
  { value: 'return', label: 'Return' },
  { value: 'exchange', label: 'Exchange' },
];

const emptyForm = {
  kind: 'cancellation', label: '', description: '',
  requiresNote: false, sortOrder: 0, isActive: true,
};

/**
 * The reasons buyers pick from when cancelling an order or asking for a return.
 *
 * Curating them here — rather than letting buyers type free text — is what
 * makes cancellations and returns reportable by cause.
 */
const Reasons = () => {
  const toast = useToast();
  const list = useList(reasonApi.list, { kind: '' });

  const [form, setForm] = useState(null);
  const [retire, setRetire] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!form.label.trim()) return toast.error('Give the reason a label');

    setBusy(true);
    try {
      if (form._id) await reasonApi.update(form._id, form);
      else await reasonApi.create(form);
      toast.success(form._id ? 'Reason updated' : 'Reason added');
      setForm(null);
      list.reload();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
    return undefined;
  };

  const doRetire = async () => {
    setBusy(true);
    try {
      const res = await reasonApi.remove(retire._id);
      toast.success(res.message);
      setRetire(null);
      list.reload();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const columns = [
    {
      key: 'label',
      label: 'Reason',
      render: row => (
        <div>
          <div style={{ fontWeight: 600 }}>{row.label}</div>
          {row.description && <div className="text-sm text-muted">{row.description}</div>}
        </div>
      ),
    },
    {
      key: 'kind',
      label: 'Shown for',
      render: row => <Badge status="info" tone="info" dot={false}>{(KINDS.find(k => k.value === row.kind) || {}).label || row.kind}</Badge>,
    },
    {
      key: 'requiresNote',
      label: 'Needs a note',
      align: 'center',
      render: row => (row.requiresNote ? 'Yes' : <span className="text-muted">No</span>),
    },
    { key: 'sortOrder', label: 'Order', align: 'center' },
    {
      key: 'isActive',
      label: 'Status',
      render: row => <Badge status={row.isActive ? 'active' : 'draft'}>{row.isActive ? 'Live' : 'Retired'}</Badge>,
    },
    {
      key: 'actions',
      label: '',
      align: 'right',
      render: row => (
        <div className="row-actions">
          <Button size="sm" variant="ghost" onClick={() => setForm({ ...row })}>Edit</Button>
          {row.isActive && (
            <Button size="sm" variant="danger" onClick={() => setRetire(row)}>Retire</Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <>
      <PageHead
        title="Cancellation & return reasons"
        subtitle="What buyers can pick from when they cancel an order or ask for a return or exchange."
        actions={<Button icon="＋" onClick={() => setForm({ ...emptyForm })}>New reason</Button>}
      />

      <div className="filters">
        <select
          className="select"
          style={{ maxWidth: 220 }}
          value={list.filters.kind}
          onChange={e => list.setFilter('kind', e.target.value)}
        >
          <option value="">All lists</option>
          {KINDS.map(k => <option key={k.value} value={k.value}>{k.label}</option>)}
        </select>
        <div className="grow" />
        <span className="text-muted text-sm">{list.meta ? `${num(list.meta.total)} reasons` : ''}</span>
      </div>

      <DataTable columns={columns} rows={list.rows} loading={list.loading} meta={list.meta} onPage={list.setPage} />

      <Modal
        open={!!form}
        title={form?._id ? 'Edit reason' : 'New reason'}
        onClose={() => setForm(null)}
        footer={
          <>
            <Button variant="ghost" onClick={() => setForm(null)}>Cancel</Button>
            <Button loading={busy} onClick={submit}>{form?._id ? 'Save changes' : 'Add reason'}</Button>
          </>
        }
      >
        {form && (
          <>
            <div className="field">
              <label>Shown for</label>
              <select
                className="select"
                value={form.kind}
                disabled={!!form._id}
                onChange={e => setForm({ ...form, kind: e.target.value })}
              >
                {KINDS.map(k => <option key={k.value} value={k.value}>{k.label}</option>)}
              </select>
              {form._id && <div className="hint">A reason cannot move between lists once buyers have used it.</div>}
            </div>

            <Input
              label="Label"
              value={form.label}
              onChange={e => setForm({ ...form, label: e.target.value })}
              placeholder="Ordered by mistake"
            />
            <Textarea
              label="Description (optional)"
              value={form.description}
              onChange={e => setForm({ ...form, description: e.target.value })}
              rows={2}
              placeholder="Extra wording shown under the option in the app."
            />

            <Input
              label="Sort order"
              type="number"
              value={form.sortOrder}
              onChange={e => setForm({ ...form, sortOrder: Number(e.target.value) })}
              hint="Lower numbers appear first."
            />

            <div className="col" style={{ gap: 11 }}>
              <Switch
                checked={form.requiresNote}
                onChange={v => setForm({ ...form, requiresNote: v })}
                label="Make the buyer add a note as well"
              />
              <Switch
                checked={form.isActive}
                onChange={v => setForm({ ...form, isActive: v })}
                label="Offer this option in the app"
              />
            </div>
          </>
        )}
      </Modal>

      <ConfirmDialog
        open={!!retire}
        title="Retire this reason?"
        message={`"${retire?.label}" will stop appearing in the app. Cancellations and returns already recorded against it keep their wording.`}
        confirmLabel="Retire"
        loading={busy}
        onConfirm={doRetire}
        onClose={() => setRetire(null)}
      />
    </>
  );
};

export default Reasons;
